import type { CreatePublicationRequest } from "@/api/hooks";

/**
 * The publication request body, and the one thing the browser still owns
 * about publication: the idempotency key.
 *
 * WHAT THIS DIRECTORY NO LONGER HOLDS. Until the GitHub App publication
 * identity, `Request publication` first had to establish the clinician's own
 * delegated GitHub connection — a per-user OAuth round trip, a stored token,
 * a refresh, a "Connected as …" menu section and a "Reconnect" recovery. The
 * service now publishes as the ENVIRONMENT's GitHub App installation: the
 * signed-in Entra user's session and Editor role are the whole precondition,
 * the requester is recorded on the immutable publication row and in the pull
 * request the App opens, and there is no GitHub account to connect, refresh
 * or reconnect anywhere in this application.
 *
 * WHY THE KEY IS PERSISTED. The contract requires `idempotencyKey` to be
 * "client-minted at intent": a retry of the same publication — after a 503
 * from the publishing service, a dropped connection, a reload — must replay
 * the SAME key with the same body, which the service answers with the
 * original record instead of a second branch and pull request. So the key is
 * minted the first time a candidate is requested and kept, per candidate, in
 * `sessionStorage` (same-tab, dies with the tab, no clinical content: a
 * candidate id and a UUID). A different candidate is a different publication
 * and starts clean; a completed publication clears its key so a later
 * publication of the same candidate mints its own.
 */
const STORAGE_PREFIX = "oneliv.editor.publishing.idempotencyKey.";

/** What the caller (#9 / #518) asks to publish. */
export interface PublicationIntentInput {
  projectId: string;
  candidate: CreatePublicationRequest["candidate"];
  /** Head guard on a re-publish; absent for a first publication. */
  expectedHeadSha?: string | null;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Storage can be unavailable outright — a hardened profile, a blocked
    // third-party context. The request still carries a fresh key; only the
    // cross-reload replay guarantee degrades, and the service's own
    // deterministic branch and adoption rules still refuse a duplicate.
    return null;
  }
}

function storageKey(projectId: string, candidateId: string): string {
  return `${STORAGE_PREFIX}${projectId}.${candidateId}`;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The idempotency key for this candidate's publication: the one minted at the
 * first request if it survived, otherwise a fresh UUID that is stored for the
 * retries to come.
 */
export function idempotencyKeyFor(projectId: string, candidateId: string): string {
  const store = storage();
  const key = storageKey(projectId, candidateId);
  try {
    const stored = store?.getItem(key);
    if (typeof stored === "string" && UUID.test(stored)) {
      return stored;
    }
  } catch {
    /* fall through to a fresh key */
  }
  const minted = crypto.randomUUID();
  try {
    store?.setItem(key, minted);
  } catch {
    /* a quota failure must not take the click down with it */
  }
  return minted;
}

/** A publication completed (or was replayed): the next one mints its own key. */
export function clearIdempotencyKey(projectId: string, candidateId: string): void {
  try {
    storage()?.removeItem(storageKey(projectId, candidateId));
  } catch {
    /* nothing to undo */
  }
}

/**
 * The exact body `POST /v1/projects/{projectId}/publications` receives.
 *
 * Session + role is the whole precondition, so there is nothing to "ensure"
 * before the POST and no outcome other than the request itself: the service
 * answers a caller its roles do not authorize with `forbidden`, and a lane
 * whose publishing identity is unavailable with a typed 503 the caller cannot
 * repair and need not (`classifyPublicationFailure`).
 */
export function publicationRequestFor(
  input: PublicationIntentInput,
): CreatePublicationRequest {
  return {
    candidate: input.candidate,
    idempotencyKey: idempotencyKeyFor(
      input.projectId,
      input.candidate.candidateId,
    ),
    ...(input.expectedHeadSha === undefined
      ? {}
      : { expectedHeadSha: input.expectedHeadSha }),
  };
}
